/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { useMemo } from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiShowFor, EuiSpacer } from '@elastic/eui'

import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { isExpiredTrial, isMarketplaceCustomer } from '@modules/billing-lib/billingDetails/utils'

import SuggestedContentPanel from '@/components/Billing/BillingOverview/components/SuggestedContentPanel'

import FeaturesOverview from '../components/FeaturesOverview'
import PrepaidAccountDetailsPanel from '../components/PrepaidAccountDetailsPanel'

import MarketplaceTrial from './MarketplaceTrial'
import ExpiredTrial from './ExpiredTrial'
import ActiveOrNotStartedTrial from './ActiveOrNotStartedTrial'

interface Props {
  organizationId: string
}

const TrialOverview = ({ organizationId }: Props) => {
  const billingDetails = useBillingDetails()

  const getTrialOverview = useMemo(() => {
    if (!billingDetails.data) {
      return null
    }

    if (isMarketplaceCustomer(billingDetails.data)) {
      return <MarketplaceTrial organizationId={organizationId} />
    }

    if (isExpiredTrial(billingDetails.data)) {
      return <ExpiredTrial />
    }

    return <ActiveOrNotStartedTrial organizationId={organizationId} />
  }, [billingDetails.data, organizationId])

  return (
    <EuiFlexGroup gutterSize='m'>
      <EuiFlexItem grow={9}>
        {getTrialOverview}
        <EuiSpacer size='m' />
        <FeaturesOverview />
        <EuiSpacer size='m' />
        <PrepaidAccountDetailsPanel hasBorder={true} organizationId={organizationId} />
        <EuiSpacer size='m' />
        <EuiShowFor sizes={['xs', 's', 'm']}>
          <EuiFlexItem grow={3}>
            <SuggestedContentPanel
              hasBorder={true}
              data-test-id='billing-details-summary.suggested-content'
              showEcuMessage={true}
            />
          </EuiFlexItem>
        </EuiShowFor>
      </EuiFlexItem>
      <EuiShowFor sizes={['l', 'xl']}>
        <EuiFlexItem grow={3}>
          <SuggestedContentPanel
            hasBorder={true}
            data-test-id='billing-details-summary.suggested-content'
            showEcuMessage={true}
          />
        </EuiFlexItem>
      </EuiShowFor>
    </EuiFlexGroup>
  )
}

export default TrialOverview
